import type { FC, ReactNode } from "react";
import { useCallback, useMemo } from "react";

import { Link } from "@mui/material";

import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import PlayerIcon from "assets/icons/outlined/player.svg?react";
import StarIcon from "assets/icons/outlined/star.svg?react";

import type { FullTeamModel, MatchPlayerModel, PlayerResolverWithStats } from "shared/types";
import { genericPlayerId } from "shared/types";
import { ImageSizesEnum, Img, Text } from "shared/uikit";
import { TextVariantsEnum } from "shared/uikit/types";

import { Card, EditInfoCard } from "..";

const TableColumn: FC<{ children: ReactNode }> = ({ children }) => (
	<div className="w-4 h-4 flex justify-center">{children}</div>
);

interface MatchDetailsTableProps {
	homeTeam: FullTeamModel;
	awayTeam: FullTeamModel;
	teamPageUrl: string;
	playerPageUrl: string;
}

const MatchDetailsTable: FC<MatchDetailsTableProps> = ({ homeTeam, awayTeam, teamPageUrl, playerPageUrl }) => {
	const { t } = useTranslation();

	const tableHeader = useMemo(
		() => (
			<div className="bg-gray-50 -ml-3 pl-5 pr-6 py-2 w-[calc(100%+1.5rem)] flex items-center justify-between max-sm:pl-4 max-sm:pr-4">
				<div className="flex items-center gap-3 w-1/2 min-w-0">
					<Text variants={TextVariantsEnum.H6} className="text-gray-400">
						#
					</Text>
					<Text variants={TextVariantsEnum.H7} className="text-gray-400">
						{t("players")}
					</Text>
				</div>
				<div className="flex items-center gap-6 justify-end w-1/2 max-sm:gap-3">
					<TableColumn>
						<Card />
					</TableColumn>
					<TableColumn>
						<Card red />
					</TableColumn>
					<TableColumn>
						<PlayerIcon className="w-4 h-4 svg-paths:stroke-gray-400" />
					</TableColumn>
					<TableColumn>
						<StarIcon className="w-4 h-4 svg-paths:fill-gray-400" />
					</TableColumn>
				</div>
			</div>
		),
		[t]
	);

	const renderPlayer = useCallback((player: PlayerResolverWithStats, team: FullTeamModel) => {
		const playerName = player?.id === genericPlayerId ? `${team?.name} Player` : player?.name;
		return (
			<div className="flex items-center gap-2 min-w-0">
				<Img src={player.avatar} alt={playerName} size={ImageSizesEnum.Small24} circle />
				<Text variants={TextVariantsEnum.Caption3} className="truncate">
					{playerName}
				</Text>
			</div>
		);
	}, []);

	const renderTeamStatus = useCallback(
		(team: FullTeamModel) => (
			<>
				<Link
					className="flex items-center justify-center gap-2 no-underline max-sm:justify-start"
					to={`${teamPageUrl}/${team.id}`}
					component={RouterLink}
				>
					<Img src={team.logo || team.club?.logo} alt={team.name} size={ImageSizesEnum.Small24} circle />
					<Text variants={TextVariantsEnum.Caption3} className="truncate">
						{team.name}
					</Text>
				</Link>
				{(team.players as MatchPlayerModel[]).map(player => (
					<div className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg" key={player.id}>
						{player?.id !== genericPlayerId ? (
							<Link
								className="flex items-center gap-3 w-1/2 min-w-0 no-underline max-sm:gap-2"
								to={`${playerPageUrl}/${player.id}`}
								component={RouterLink}
							>
								<Text variants={TextVariantsEnum.Caption3}>{player?.jerseyNumber ?? "-"}</Text>
								{renderPlayer(player, team)}
							</Link>
						) : (
							<div className="flex items-center gap-3 w-1/2 min-w-0 no-underline max-sm:gap-2">
								<Text variants={TextVariantsEnum.Caption3}>-</Text>
								{renderPlayer(player, team)}
							</div>
						)}
						<div className="flex items-center justify-end gap-6 w-1/2 max-sm:gap-3">
							<TableColumn>
								<Text variants={TextVariantsEnum.Caption3}>{player.yellowCardsReceived}</Text>
							</TableColumn>
							<TableColumn>
								<Text variants={TextVariantsEnum.Caption3}>{player.redCardsReceived}</Text>
							</TableColumn>
							<TableColumn>
								<Text variants={TextVariantsEnum.Caption3}>{player.goalsScored}</Text>
							</TableColumn>
							<TableColumn>
								<Text variants={TextVariantsEnum.Caption3}>{player.starsReceived}</Text>
							</TableColumn>
						</div>
					</div>
				))}
			</>
		),
		[teamPageUrl, playerPageUrl, renderPlayer]
	);

	return (
		<EditInfoCard title="Match details" allowEdit={false}>
			<div className="mt-3 mb-1 flex flex-col gap-3">
				{tableHeader}
				{!!(homeTeam?.players as PlayerResolverWithStats[])?.length && renderTeamStatus(homeTeam)}
				{!!(awayTeam?.players as PlayerResolverWithStats[])?.length && renderTeamStatus(awayTeam)}
			</div>
		</EditInfoCard>
	);
};

export default MatchDetailsTable;
